import { useEffect, useState } from 'react';
import { NavLink, Outlet, matchPath, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Blocks,
  Boxes,
  ArrowLeftRight,
  BarChart3,
  ChevronDown,
  Download,
  FileText,
  Fingerprint,
  KeyRound,
  Filter,
  House,
  LayoutDashboard,
  LayoutTemplate,
  List,
  Megaphone,
  LogOut,
  Mail,
  MailX,
  Menu,
  Palette,
  Send,
  ShieldCheck,
  Sparkles,
  ShoppingCart,
  ScrollText,
  Star,
  Tag,
  Ticket,
  User,
  UserX,
  Users,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { AiAssistant } from '../ai/AiAssistant';
import { HealthRail } from '../HealthRail';
import { SkipLink } from '../SkipLink';
import { ChangeNameDialog } from '../ChangeNameDialog';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { userName } from '../../lib/format';
import { apiFetch } from '../../lib/api';
import { APP_NAME } from '../../lib/appName';
import type { ChecklistResponse } from '../../types/api';

type NavItem = { to: string; label: string; icon: LucideIcon; end?: boolean };
/** `startOpen: false` groups are folded until opened or until one of their pages is active. */
type NavGroup = { heading: string; items: NavItem[]; startOpen: boolean };

const SENDING_GROUP: NavGroup = {
  heading: 'Sending',
  startOpen: true,
  items: [
    { to: '/kampagner', label: 'Campaigns', icon: Send },
    { to: '/automatiseringer', label: 'Automations', icon: Zap },
    { to: '/skabeloner', label: 'Templates', icon: LayoutTemplate },
    { to: '/indstillinger/delte-blokke', label: 'Shared blocks', icon: Blocks },
  ],
};

const AUDIENCE_GROUP: NavGroup = {
  heading: 'Audience',
  startOpen: true,
  items: [
    { to: '/kontakter', label: 'Contacts', icon: Users },
    { to: '/afmeldte', label: 'Unsubscribed', icon: UserX },
    { to: '/bounces', label: 'Bounces', icon: MailX },
    { to: '/indstillinger/lister', label: 'Lists', icon: List },
    { to: '/kundemaerkater', label: 'Customer tags', icon: Tag },
    { to: '/indstillinger/segmenter', label: 'Segments', icon: Filter },
    { to: '/indstillinger/signup-forms', label: 'Signup popup', icon: Megaphone },
    { to: '/indstillinger/forms', label: 'Forms', icon: Megaphone },
    { to: '/indstillinger/landing-pages', label: 'Landing pages', icon: Megaphone },
  ],
};

const RESULTS_GROUP: NavGroup = {
  heading: 'Results',
  startOpen: true,
  items: [
    { to: '/statistik', label: 'Statistics', icon: BarChart3, end: true },
    { to: '/statistik/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/statistik/tilmeldingskilder', label: 'Signup sources', icon: ShieldCheck },
    { to: '/koebere', label: 'Buyers', icon: ShoppingCart },
    { to: '/anmeldelser', label: 'Reviews', icon: Star },
    { to: '/kuponer', label: 'Coupons', icon: Ticket },
  ],
};

const MAILCHIMP_GROUP: NavGroup = {
  heading: 'Move from Mailchimp',
  startOpen: false,
  items: [
    { to: '/indstillinger/mailchimp-import', label: 'Mailchimp import', icon: Download },
    {
      to: '/indstillinger/overgang-fra-mailchimp',
      label: 'Mailchimp cutover',
      icon: ArrowLeftRight,
    },
  ],
};

const SETTINGS_GROUP: NavGroup = {
  heading: 'Settings',
  startOpen: false,
  items: [
    { to: '/indstillinger/afsendere', label: 'Senders', icon: Mail },
    { to: '/indstillinger/woo', label: 'WooCommerce', icon: ShoppingCart },
    { to: '/indstillinger/brugere', label: 'Users', icon: User },
    { to: '/indstillinger/brand-kit', label: 'Brand kit', icon: Palette },
    { to: '/indstillinger/samtykketekster', label: 'Consent texts', icon: FileText },
    { to: '/indstillinger/ai', label: 'AI assistant', icon: Sparkles },
    { to: '/indstillinger/custom-objects', label: 'Custom objects', icon: Boxes },
  ],
};

/**
 * The "Move from Mailchimp" group is shown until the DMARC cutover checklist
 * (plan §4a, docs/decisions.md "DMARC-overgang 28/9") is fully ticked — there is
 * no explicit "cutover complete" flag in the data model, so completion is derived
 * as "every checklist item is checked". Once true, the group hides itself since
 * the move is done and the pages are no longer relevant day to day.
 */
function useMailchimpCutoverPending(): boolean {
  const { data } = useQuery({
    queryKey: ['dmarc-cutover', 'checklist'],
    queryFn: () => apiFetch<ChecklistResponse>('/dmarc-cutover/checklist'),
    staleTime: 60_000,
  });
  if (!data) return true;
  return !data.items.every((item) => item.checked);
}

/** "Security" (passkeys) is listed only when the API has passkeys on. */
function usePasskeysAvailable(): boolean {
  const { data } = useQuery({
    queryKey: ['auth', 'passkeys', 'available'],
    queryFn: () => apiFetch<{ enabled: boolean }>('/auth/passkeys/available'),
    retry: false,
    staleTime: 60_000,
  });
  return data?.enabled === true;
}

/**
 * "API tokens" is listed only for admins and only when the API has the
 * feature (`GET /api-tokens` answers 404 when API_TOKENS_ENABLED is off).
 */
function useApiTokensAvailable(isAdmin: boolean): boolean {
  const { data } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => apiFetch<{ tokens: unknown[] }>('/api-tokens'),
    enabled: isAdmin,
    retry: false,
    staleTime: 60_000,
  });
  return isAdmin && data !== undefined;
}

const OPEN_GROUPS_KEY = 'nav-open-groups';

function readOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-2.5 px-2.5 py-1.5 rounded-[7px] no-underline',
    isActive
      ? 'bg-card text-text font-semibold ring-1 ring-border'
      : 'text-muted hover:bg-border-soft hover:text-text',
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end ?? false} className={navLinkClass}>
      {({ isActive }) => (
        <>
          <Icon
            size={16}
            strokeWidth={1.8}
            aria-hidden="true"
            className={cn('shrink-0', isActive ? 'text-accent' : 'text-muted')}
          />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

function NavGroupList({
  group,
  open,
  onToggle,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: () => void;
}) {
  const listId = `nav-group-${group.heading.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <div>
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={onToggle}
          className="w-full flex items-center justify-between px-2.5 pt-3.5 pb-1 rounded-[6px] text-[11px] font-medium text-muted hover:text-text"
        >
          {group.heading}
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={cn('transition-transform duration-150', open && 'rotate-180')}
          />
        </button>
      </h2>
      <ul id={listId} hidden={!open} className="flex flex-col gap-px list-none m-0 p-0">
        {group.items.map((item) => (
          <li key={item.to}>
            <NavItemLink item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AppShell() {
  const { user, logout, isAdmin } = useAuth();
  const mailchimpCutoverPending = useMailchimpCutoverPending();
  const apiTokensAvailable = useApiTokensAvailable(isAdmin);
  const passkeysAvailable = usePasskeysAvailable();
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState(readOpenGroups);

  const settingsItems: NavItem[] = [
    ...SETTINGS_GROUP.items,
    ...(apiTokensAvailable
      ? [{ to: '/indstillinger/api-tokens', label: 'API tokens', icon: KeyRound }]
      : []),
    ...(passkeysAvailable
      ? [{ to: '/indstillinger/sikkerhed', label: 'Security', icon: Fingerprint }]
      : []),
    ...(isAdmin ? [{ to: '/indstillinger/audit', label: 'Audit log', icon: ScrollText }] : []),
  ];

  const groups: NavGroup[] = [
    SENDING_GROUP,
    AUDIENCE_GROUP,
    RESULTS_GROUP,
    ...(mailchimpCutoverPending ? [MAILCHIMP_GROUP] : []),
    { ...SETTINGS_GROUP, items: settingsItems },
  ];

  // Until the user folds a group themselves, it opens when it holds the current page.
  const isOpen = (group: NavGroup): boolean =>
    openGroups[group.heading] ??
    (group.startOpen ||
      group.items.some((item) => matchPath({ path: item.to, end: false }, pathname)));

  const toggle = (group: NavGroup) => {
    const next = { ...openGroups, [group.heading]: !isOpen(group) };
    setOpenGroups(next);
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
    } catch {
      // Storage blocked: the choice just lasts until reload.
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  // Close the mobile menu after navigating.
  useEffect(() => setMenuOpen(false), [pathname]);

  const name = userName(user) ?? user?.userId ?? 'Unknown';
  const initials = name.slice(0, 1).toUpperCase();
  const who = `${user?.email ?? user?.userId ?? 'Unknown'} · ${user?.role ?? '–'}`;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-bg text-text text-sm">
      <SkipLink />
      <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-border">
        <span className="font-semibold text-base tracking-tight">{APP_NAME}</span>
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto h-10 px-3 rounded-[8px] border border-border bg-card flex items-center gap-2 font-medium"
        >
          {menuOpen ? <X size={16} aria-hidden="true" /> : <Menu size={16} aria-hidden="true" />}
          Menu
        </button>
      </div>
      <nav
        id="main-nav"
        aria-label="Main menu"
        className={cn(
          'md:flex md:w-[232px] md:flex-shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto',
          'flex-col gap-0.5 px-3 pt-4 pb-3 md:border-r border-b md:border-b-0 border-border',
          menuOpen ? 'flex' : 'hidden',
        )}
      >
        <div className="flex items-baseline gap-2 px-2.5 pb-[18px] pt-0.5 max-md:hidden">
          <span className="font-semibold text-base tracking-tight">{APP_NAME}</span>
          <span className="text-xs text-muted">NoPain</span>
        </div>
        <NavItemLink item={{ to: '/', label: 'Overview', icon: House, end: true }} />
        {groups.map((group) => (
          <NavGroupList
            key={group.heading}
            group={group}
            open={isOpen(group)}
            onToggle={() => toggle(group)}
          />
        ))}
        <div className="mt-auto pt-4">
          <div className="flex items-center gap-2.5 border-t border-border p-2.5 pt-3">
            <span
              aria-hidden="true"
              className="grid h-7 w-7 flex-none place-items-center rounded-full bg-accent-soft text-xs font-semibold text-link"
            >
              {initials}
            </span>
            <div className="min-w-0 flex-1" title={who}>
              <div className="truncate text-[13px] font-semibold">{name}</div>
              <div className="truncate text-xs text-muted capitalize">{user?.role ?? '–'}</div>
            </div>
            <ChangeNameDialog current={user?.displayName ?? ''} />
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Log out"
              title="Log out"
              className="grid h-8 w-8 flex-none place-items-center rounded-[7px] text-muted hover:bg-border-soft hover:text-text"
            >
              <LogOut size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-grow min-w-0 px-4 py-5 md:px-9 md:pt-7 md:pb-10 flex flex-col gap-6"
      >
        <HealthRail />
        <Outlet />
        <AiAssistant />
      </main>
    </div>
  );
}
